import React, {
  memo,
  useCallback,
  useEffect,
  useContext,
  useMemo,
  useState,
} from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { FormGroup, Grid, Switch, Typography } from "@mui/material";
import { AuthContext } from "../../context/authContext";
import ItemsList from "../../components/ItemsList/ItemsList";
import { getAssigneeWorkingIntervals } from "../../request/task";
import { dateTimeFormat, formatDescription } from "../Board/Board";
import MembersList from "../Board/components/MembersList/MembersList";
import TextField from "@mui/material/TextField";
import Export from "./Export";
import "./styles.scss";
import FormControlLabel from "@mui/material/FormControlLabel";
import Diagrams from "./Diagrams";

export const getDuration = (item) => {
  let start = moment(item.started_at);
  let end = item.ended_at ? moment(item.ended_at) : moment();
  return moment.duration(end.diff(start)).asSeconds();
};

export const formatHoursDuration = (time) => {
  let hours = Math.floor(time / (60 * 60));
  let minutes = Math.floor((time / 60) % 60);

  return (
    <span className="nowrap">
      {hours > 0 ? (hours < 10 ? "0" + hours : hours) + "h " : ""}
      {minutes > 0 ? (minutes < 10 ? "0" + minutes : minutes) + "min " : ""}
    </span>
  );
};

export default memo(function WorkingIntervals() {
  const { currentUser } = useContext(AuthContext);

  const [sla, setSla] = useState(null);

  const [elements, setElements] = useState(null);
  const [total, setTotal] = useState(null);
  const pageSize = 20;

  const dateFormat = "YYYY-MM-DD";

  const [dateFrom, setDateFrom] = useState(moment().format(dateFormat));
  const [dateTo, setDateTo] = useState(moment().format(dateFormat));
  const [assigneeId, setAssigneeId] = useState(null);

  const [totalTime, setTotalTime] = useState(null);

  useEffect(() => {
    if (currentUser?.id) {
      setAssigneeId([currentUser?.id]);
    }
  }, [currentUser]);

  useEffect(() => {
    const get = async () => {
      await getData({});
    };

    get().catch((err) => console.error(err));
  }, [assigneeId, dateFrom, dateTo]);

  const getData = useCallback(
    async (data) => {
      if (!assigneeId) return;
      try {
        let params = {
          assignee_id: assigneeId,
          date_from: dateFrom ?? moment().format("YYYY-MM-DD"),
          date_to: dateTo,
        };

        params.page = data.page?.number ?? 1;
        params.page_size = data.page?.size ?? pageSize;

        if (data.sort) {
          params.sort_by = data.sort.replace("-", "");
          if (data.sort.indexOf("-") >= 0) {
            params.sort_order = "desc";
          }
        }

        const response = await getAssigneeWorkingIntervals(params).catch(
          (err) => {},
        );
        setElements(
          response?.data?.intervals.map((interval) => {
            return {
              ...interval,
              name: interval.task?.name,
            };
          }),
        );
        setSla({
          by_days: response?.data?.time_by_days,
          by_users: response?.data?.time_by_users,
        });
        setTotal(response?.data?.total);
        setTotalTime(response?.data?.total_time);
      } catch (e) {
        console.log(e);
      }
    },
    [assigneeId, dateFrom, dateTo],
  );

  const tableHeaders = useMemo(() => {
    return [
      {
        field: "started_at",
        headerName: "Date",
        sortable: true,
        type: "string",
        maxWidth: 150,
        flex: 1,
        valueGetter: (params) =>
          params.value
            ? `${moment(params.value).format(dateTimeFormat)} - ${(params.row.ended_at ? moment(params.row.ended_at) : moment()).format(dateTimeFormat)}`
            : "",
      },
      {
        field: "name",
        headerName: "Number",
        sortable: true,
        flex: 1,
        type: "string",
        renderCell: (item) => (
          <>
            {item.row?.task?.permalink_url && (
              <Link to={item.row?.task?.permalink_url} target="_blank">
                <span
                  dangerouslySetInnerHTML={{
                    __html: formatDescription(item.value),
                  }}
                />
              </Link>
            )}
          </>
        ),
      },
      {
        field: "duration",
        headerName: "Duration",
        sortable: true,
        type: "string",
        maxWidth: 150,
        flex: 1,
        renderCell: (params) =>
          formatHoursDuration(params.value ?? getDuration(params.row)),
      },

      {
        field: "assignee",
        headerName: "Assignee",
        sortable: true,
        maxWidth: 200,
        flex: 1,
        valueGetter: (params) => params.value?.email,
      },
      {
        field: "status",
        headerName: "Status now",
        sortable: false,
        type: "string",
        maxWidth: 100,
        flex: 1,
        renderCell: (item) => item.row.task?.status,
      },
    ];
  }, []);

  const onChangeAssigneeHandler = useCallback((params) => {
    setAssigneeId(params.map((el) => el.value));
  }, []);

  const handleDateChange = useCallback((code, value) => {
    switch (code) {
      case "date_from":
        setDateFrom(value);
        break;
      case "date_to":
        setDateTo(value);
        break;
      default:
        break;
    }
  }, []);

  const [showDiagram, setShowDiagram] = useState(false);
  const switchDiagram = useCallback(() => {
    setShowDiagram(!showDiagram);
  }, []);

  return (
    <div className="working-time-wrapper">
      <Grid container spacing="15px">
        <Grid item xs={12} md={12}>
          <div className="flex-start mb-15">
            <div>
              <Typography variant="h6">In Progress Working Time</Typography>
            </div>
            <div className="pl-15"></div>
          </div>
        </Grid>
        <Grid item xs={12} md={3}>
          {
            <TextField
              type="date"
              label="Date from"
              defaultValue={moment().format(dateFormat)}
              size="small"
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(e) => handleDateChange("date_from", e.target.value)}
              autoComplete="off"
            />
          }
        </Grid>
        <Grid item xs={12} md={3}>
          {
            <TextField
              type="date"
              label="Date to (including)"
              defaultValue={moment().format(dateFormat)}
              size="small"
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(e) => handleDateChange("date_to", e.target.value)}
              autoComplete="off"
            />
          }
        </Grid>
        <Grid item xs={12} md={6}>
          {
            <MembersList
              multiple={true}
              defaultAssignee={currentUser?.id}
              onChange={onChangeAssigneeHandler}
            />
          }
        </Grid>
      </Grid>
      {elements && (
        <div style={{ margin: "20px 0" }}>
          <div className={"flex"}>
            <small>
              if task is in progress yet we take current date as a last end date
              in intervals
            </small>

            <FormGroup>
              <FormControlLabel
                control={<Switch onChange={switchDiagram} />}
                label="Switch to Diagrams"
              />
            </FormGroup>
          </div>

          {!showDiagram && (
            <ItemsList
              items={elements}
              headers={tableHeaders}
              totalCount={total}
              defaultPageSize={pageSize}
              server={true}
              getItems={getData}
              gridId="working-time"
            />
          )}
        </div>
      )}
      {totalTime > 0 && (
        <div>
          <h3>Total time: {formatHoursDuration(totalTime)}</h3>
        </div>
      )}

      {showDiagram && sla && (
        <Diagrams sla={sla} dateFromValue={dateFrom} dateToValue={dateTo} />
      )}

      {!!elements?.length && (
        <div style={{ margin: "40px 0" }}>
          <Export
            dateFromValue={dateFrom}
            dateToValue={dateTo}
            assigneeIdsArray={assigneeId}
          />
        </div>
      )}
    </div>
  );
});
